/** @description 流程图编辑页面 */
import React, { useEffect, useMemo, useState, useRef, useReducer } from 'react';

import { Button, Col, Row, Radio, message, Input, Space } from 'antd';

import ReactFlowArea from '@/components/react-flow';
import { Edge, Node } from 'react-flow-renderer';
import NodeForm, { HandleEventMapByT, initNodeTableData } from '@/components/node-form';
import EdgeForm from '@/components/edge-form';
import Loading from '@/components/loading';

import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DoubleLeftOutlined } from '@ant-design/icons';

import { Access, useAccess } from 'umi';

import {
  DataType,
  NodeSelectItem,
  TableData,
  ConditionItem,
  ActionValueType,
  ModelDeleteEdge,
  Kind,
} from '@/components/index';

import { useDispatch, Prompt, useSelector, history, useParams } from 'umi';
import { useDebounceFn } from 'ahooks';
import CommonRule from '@/components/common-rule';
import TitleTooltip from '@/components/title-tooltip';
import ActionEditDrawer from '@/components/action-edit-drawer';

import { request } from '@/utils/request';
import moment from 'moment';
import DB, { DBValueProps, HiddenItemProps } from './db';
import { ActionMap } from '@/components/node-default';
import _ from 'lodash';
import ConditionEditModal from '@/components/EditCondition/index';
import JSONDrawer from '@/components/JSONDrawer';

const codeMap: any = {
  匹配ABCD: (text: string) => {
    const RegExp = /abcdeABCDE/;
    return RegExp.test(text);
  },
  匹配年龄: (text: string) => {
    const RegExp = /.*[0-9]{1,}.*/;
    return RegExp.test(text);
  },
};

type BaseConfig = { name: string; platform: 1 | 2; messageEngine: string; };
const baseConfigReducer = (state: BaseConfig, action: { type: 'update' | 'init' ; prop: string; data: string | number | BaseConfig; }) => {
  switch(action.type) {
    case 'init':
      return { ...(action.data as BaseConfig) }
    case 'update':
      return { ...state, [action.prop]: action.data };
  }
}

const OverviewFlow: React.FC = () => {
  const [isPrompt, setIsPrompt] = useState<boolean>(true);
  const childRef: any = useRef(null);
  const nodeFormRef: any = React.createRef();
  const commonFormRef: any = React.createRef();

  const params: { id?: string } = useParams();

  const [currentNode, setCurrentNode] = useState<Node | null>(null);
  const [currentEdge, setCurrentEdge] = useState<Edge | null>(null);
  const [isShowJSON, setIsShowJSON] = useState<boolean>(false);
  const [json, setJson] = useState<any>(null);
  const [drawerTitle, setDrawerTitle] = useState<string>('');
  const [isEdit, setIsEdit] = useState<boolean>(false);

  const [workflowDetail, setWorkflowDetail] = useState<any>({});
  const [tableData, setTableData] = useState<TableData>({});

  const [baseConfig, dispatchBaseConfig] = useReducer(baseConfigReducer, {
    platform: 1,
    name: '',
    messageEngine: 'juzi',
  })
  const [loading, setLoading] = useState<boolean>(false);
  const [mouseEnterEditArea, setMouseEnterEditArea] = useState<boolean>(false);

  const nodeSelectList = useSelector((state: any) => state.open.nodeSelectList);
  const nodeHiddenMap = useSelector((state: any) => state.open.nodeHiddenMap);
  const deleteEdge = useSelector((state: any) => state.open.deleteEdge);
  const copyItem = useSelector((state: any) => state.open.copyItem);
  const selectedNode = useSelector((state: any) => state.open.selectedNode);
  const selectedEdge = useSelector((state: any) => state.open.selectedEdge);
  const dispatch = useDispatch();

  const access = useAccess();

  /** @description 复制节点内容 */
  useEffect(() => {
    if (copyItem.isCopy) {
      let { item, nodeId, currentNodeType } = copyItem.data;
      let kind: Kind = copyItem.data.kind;
      let n = _.find(nodeSelectList, (o) => o.key === nodeId);
      let n_tableData = tableData[nodeId] ?? initNodeTableData(nodeId, n.label);
      if (n.type === 'globalNode' && kind !== 'onEnter') {
        message.warning('全局节点只能配置初入内容！');
      } else if (
        kind === 'onReceive' &&
        n_tableData.isOnReceive &&
        n_tableData.onReceive.length
      ) {
        message.warning('该节点已配置匹配规则（可手动调整）！');
      } else if (kind && item) {
        // 复制 onEnter onIdle onReceive
        let new_item = {
          ...item,
          key: `${kind}-${moment(new Date()).valueOf()}`,
        };

        const kindItems = n_tableData[kind] ?? [];
        const isKindOpen = 'is' + kind[0].toUpperCase() + kind.substr(1);
        const newItem = {
          ...n_tableData,
          [kind]: kind !== 'onReceive' ? [...kindItems, new_item] : [new_item],
          [isKindOpen]: true,
        };
        const newData = {
          ...tableData,
          [nodeId]: newItem,
        };
        setTableData(newData);
      }
    }
  }, [copyItem.isCopy]);

  useEffect(() => {
    if (params && params?.id && params.id !== ':id') {
      getWorkflowDetail();
    } else {
      setTimeout(() => {
        // 非编辑 默认放置开始节点
        addNode('startNode');
      }, 300);
    }
    dispatch({ type: 'open/apiGetMiniProgramConfigList' });
    dispatch({ type: 'open/apiGetLinkConfigList' });
    dispatch({ type: 'open/apiGetChannelList' });
    return () => {
      dispatch({ type: 'open/initWorkflowConfig', payload: null });
      dispatch({ type: 'open/handleEdgesChange', payload: { edges: [] } });
      dispatch({ type: 'open/handleSelected', payload: { selectedNode: '', selectedEdge: '' } });
    };
  }, []);

  /** @description */
  const { run: dispatchTableDataChange } = useDebounceFn(
    () => {
      dispatch({ type: 'open/sendTableDataChange', payload: tableData });
    },
    { wait: 200 },
  );

  useEffect(() => dispatchTableDataChange, [tableData]);

  /** edge被删除，更新condition数据 */
  useEffect(() => {
    if (!deleteEdge.length) return;
    let newData = { ...tableData };
    deleteEdge.forEach((item: ModelDeleteEdge) => {
      const arr = newData[item.source]?.condition ?? [];
      const newItem = {
        ...newData[item.source],
        condition: _.remove(arr, (o) => o.key !== item.target),
      };
      newData = {
        ...newData,
        [item.source]: newItem,
      };
    });

    setTableData(newData);
  }, [deleteEdge]);

  const rightContainerWidth = useMemo(() => {
    return (mouseEnterEditArea || selectedNode || selectedEdge) ? 500 : 20;
  }, [mouseEnterEditArea, selectedNode, selectedEdge]);

  const isShowNodeForm: boolean = useMemo(() => {
    const existNode: string[] = nodeSelectList.map(
      (n: NodeSelectItem) => n.key,
    );
    if (!currentNode) return false;
    return existNode.includes(currentNode.id);
  }, [currentNode, nodeSelectList]);

  /** @description 获取已经存在的流程图配置详情 */
  const getWorkflowDetail = async () => {
    const id = params.id;
    if (!id) return;
    setLoading(true);
    const db = new DB();
    await db.init();
    const hidden_config = (await db.get(parseInt(id))) as DBValueProps;
    setIsEdit(true);
    const { resp } = await request(`/local/workflow/${params.id}`);
    if (resp?.code !== 0)
      return message.warning(resp?.message ?? '该流程不存在！');
    const { common_conditions, common_onReceive, edges, nodes, tableData } =
      resp?.data.flowConfig.config;
    let nodeHiddenMap: { [key: string]: boolean[] } = {};
    let needInit = false;
    if (hidden_config) {
      for (let i = 0; i < nodes.length; i++) {
        let id = nodes[i].id;
        if (hidden_config.nodes[id] === undefined) {
          needInit = true;
          break;
        }
      }
    } else {
      needInit = true;
    }
    if (needInit) {
      nodes.forEach((n: Node) => {
        nodeHiddenMap[n.id] = [Boolean(n.hidden)];
      });
    } else {
      nodeHiddenMap = hidden_config.switch_status;
    }
    dispatch({
      type: 'open/initNodeHidden',
      payload: nodeHiddenMap,
    });

    dispatch({
      type: 'open/initWorkflowConfig',
      payload: {
        ...resp?.data.flowConfig.config,
        nodes: nodes.map((n: Node) => {
          return {
            ...n,
            data: tableData[n.id] ?? { label: n.id },
            hidden: !needInit ? hidden_config.nodes[n.id] : false,
          };
        }),
        edges: edges.map((e: Edge) => {
          return {
            ...e,
            hidden: !needInit ? hidden_config.edges[e.id] : false,
          };
        }),
      },
    });
    setTableData(tableData);
    setWorkflowDetail(resp?.data);
    dispatchBaseConfig({
      type: 'init',
      prop: '',
      data: {
        name: resp?.data.name,
        platform: resp?.data.platform,
        messageEngine: resp?.data.messageEngine
      }
    })
    setLoading(false);
  };

  /** @description 新增一个节点node */
  const addNode = (type: string) => {
    if (childRef.current) childRef.current.addNode(type);
  };

  /** @description 选中节点，右侧展示节点详情 */
  const showNodeDetail = (node: Node) => {
    setCurrentNode(node);
  };

  /** @description 选中线edge，右侧展示线详情 */
  const showEdgeDetail = (edge: Edge) => {
    setCurrentEdge(edge);
  };

  const { run: handleNodeChange } = useDebounceFn(
    (node: Node) => {
      childRef.current.setNode(node);
    },
    { wait: 200 },
  );

  const nodeChange = (node: Node) => handleNodeChange(node);

  const edgeChange = (edge: Edge) => {
    childRef.current.setEdge(edge);
  };

  const showJSON = () => {
    childRef.current.showJSON('json');
  };

  const showNodesAndEdges = () => {
    childRef.current.showJSON('nAndE');
  };

  const showJSONDrawer = (json: any, type: 'json' | 'nAndE') => {
    if (!json) {
      message.warning('请先完善流程图！');
      return;
    }

    setIsShowJSON(true);
    setDrawerTitle(type === 'json' ? '流程图生成的JSON' : '节点与边');
    if (type !== 'json') {
      setJson(json);
      return;
    }

    setJson(getJson()?.json);
  };

  const computeJson = (nodes: Node[], edges: Edge[]) => {
    let obj: any = {};
    nodes.forEach((n: Node) => {
      obj[n.id] = { content: n.data.label };
      edges.forEach((e: Edge) => {
        let edgeLabel: string = '' + (e.label ?? '');
        if (e.source === n.id && e.target) {
          if (!edgeLabel) {
            obj[n.id]['next'] = e.target;
          }
        }
      });
      obj[n.id]['condition'] = n.data.condition;
    });
    return obj;
  };

  const getJson = () => {
    const common_conditions = commonFormRef.current.conditions;
    const common_onReceive = commonFormRef.current.onReceive;

    const nodes = childRef.current.nodes;
    const edges = childRef.current.edges;

    if (!nodes.length) return null;

    // const tableData = nodeFormRef?.current?.tableData ?? {};

    const json = computeJson(nodes, edges);

    const _tableData: TableData = {};
    nodes.forEach((n: Node) => {
      _tableData[n.id] = { ...n.data, type: n.type };
    });

    let _nodes = Object.keys(json);
    let globalNodes: { name: string; label: any }[] = [];
    let otherNodes: { name: string; label: any }[] = [];
    _nodes.forEach((key) => {
      let obj = {};
      let tableItem = _tableData[key];
      if (tableItem) {
        obj = {
          ...obj,
          onEnter: handleOnEnterAndOnIdleJson(
            tableItem.onEnter,
            tableItem.isOnEnter,
          ),
          onIdle: handleOnEnterAndOnIdleJson(
            tableItem.onIdle,
            tableItem.isOnIdle,
          ),
          onReceive: handleOnReceiveJson(
            tableItem.onReceive,
            tableItem.isOnReceive,
          ),
        };
      }
      const next = json[key].next;
      const conditions = json[key].condition;
      obj = {
        ...obj,
        next: next
          ? { condition: true, node: json[key].next }
          : conditions
            ? conditions.map((c: ConditionItem) => {
              return { condition: c.condition, node: c.key };
            })
            : null,
      };
      const values = {
        name: key,
        label: json[key].content,
        ...obj,
      };
      if (tableItem.type === 'globalNode') {
        globalNodes.push(values);
        return;
      }
      otherNodes.push(values);
    });

    const result = {
      catchAll: {
        onReceive: common_onReceive,
        next: common_conditions.map((c: ConditionItem) => {
          return {
            condition: c.condition,
            node: c.key,
          };
        }),
        nodes: globalNodes,
      },
      startNode: 'introduction',
      nodes: otherNodes,
    };

    return {
      json: result,
      config: {
        common_onReceive,
        common_conditions,
        nodes: nodes.map((n: Node) => {
          return {
            id: n.id,
            height: n.height,
            width: n.height,
            type: n.type,
            // style: n.style,
            position: n.position,
            positionAbsolute: n.positionAbsolute,
          };
        }),
        edges: edges.map((e: Edge) => {
          return {
            id: e.id,
            source: e.source,
            target: e.target,
            markerEnd: e.markerEnd,
            label: e.label,
          };
        }),
        tableData: _tableData,
      },
    };
  };

  /** @description indexDB保存 节点展示配置 */
  const saveHiddenConfig = async (
    workflow_id: number,
    nodes: Node[],
    edges: Edge[],
  ) => {
    const db = new DB();
    await db.init();
    const old_config = await db.get(workflow_id);

    const switch_status: { [key: string]: boolean[] } = {};
    const _edges: HiddenItemProps = {};
    const _nodes: HiddenItemProps = {};
    nodes.forEach((n: Node) => {
      switch_status[n.id] = nodeHiddenMap[n.id] ?? [false];
      _nodes[n.id] = Boolean(n?.hidden);
    });
    edges.forEach((e: Edge) => {
      _edges[e.id] = Boolean(e?.hidden);
    });

    const maps = { switch_status: switch_status, edges: _edges, nodes: _nodes };

    if (!old_config) {
      await db.add({ value: maps, workflow_id });
      return;
    }
    await db.update(workflow_id, maps);
  };

  /** @description 保存设置 */
  const save = async () => {
    setLoading(true);
    const { nodes, edges } = childRef.current;
    const result = getJson();
    if (!result) {
      setLoading(false);
      message.warning('请先完善流程图！');
      return;
    }
    const config = result.config;
    const version = !isEdit
      ? `v${moment(new Date()).format('YYYYMMDDHHmmss')}`
      : workflowDetail.version;

    const json = { version, ...result.json };

    if (!handleJudgeData(config)) return setLoading(false);

    let { resp } = isEdit
      ? await request(`/local/workflow/${params.id}`, {
        method: 'put',
        data: {
          config,
          json,
          needUpdateConfig: true,
          status: workflowDetail?.status,
          ...baseConfig,
          updateCount: workflowDetail?.updateCount,
        },
      })
      : await request('/local/workflow', {
        method: 'post',
        data: {
          config,
          json,
          needUpdateConfig: false,
          name: baseConfig.name,
          version,
        },
      });
    if (resp?.code !== 0) {
      setLoading(false);
      message.error('保存失败！');
      return;
    }
    await saveHiddenConfig(resp.data.id, nodes, edges);
    handlePageBack();
    setLoading(false);
    message.success('保存成功！');
  };

  /** @description 校验配置信息是否完整 */
  const handleJudgeData = (config: {
    common_conditions: ConditionItem[];
    common_onReceive: DataType[];
  }) => {
    const { common_conditions, common_onReceive } = config;

    let isValid = true;

    if (common_onReceive.length > 0 && common_conditions.length === 0) {
      message.warning('请检查全局规则是否设置匹配字段');
      return false;
    }

    const nodes = childRef.current.nodes;
    function check(data: DataType[], type: 'onEnter' | 'onIdle' | 'onReceive') {
      let result = true;
      let title = HandleEventMapByT[type].label;
      for (let j = 0; j < data.length; j++) {
        const vo = data[j];
        if (['onEnter', 'onIdle'].includes(type)) {
          if (!vo.delay) {
            result = false;
            break;
          }
        }
        let prop = (ActionMap[vo.action].value ?? ActionMap[vo.action].prop) as ActionValueType;
        if (!vo[prop]) {
          result = false;
          break;
        }
      }
      return [result, title];
    }

    for (let i = 0; i < nodes.length; i++) {
      const vo = nodes[i];
      const data = vo.data;
      let _r = [true, '初入'];
      if (data.isOnEnter && data.onEnter) {
        _r = check(data.onEnter, 'onEnter');
      }
      if (data.inOnIdle && data.onIdle) {
        _r = check(data.onIdle, 'onIdle');
      }
      if (!_r[0]) {
        isValid = false;
        message.warning(`请完善${data.label}${_r[1]}内容`);
        break;
      }
    }
    return isValid;
  };

  /** @description 返回上一页面 */
  const handlePageBack = () => {
    setIsPrompt(false);
    history.goBack();
  };

  const handleOnEnterAndOnIdleJson = (data: DataType[], bool: boolean) => {
    if (!data || !data.length || !bool) return null;
    const arr = data.map((vo: DataType) => {
      let action = vo.action;
      let payload: DataType = {
        key: vo.key,
        delay: vo.delay,
        action: vo.action,
      };
      if (action === 'say') payload.text = vo.text;
      if (action === 'voice') payload.voice = vo.voice;
      if (action === 'link') payload.link = vo.link;
      if (action === 'image') payload.image = vo.image;
      if (action === 'miniProgram') payload.miniProgram = vo.miniProgram;
      if (action === 'createGroup') payload.text = vo.createGroup;
      if (action === 'reactivateGroup') payload.text = vo.reactivateGroup;
      if (action === 'channel') payload.channel = vo.channel;
      if (action === 'video') payload.video = vo.video;
      return payload;
    });
    return arr;
  };

  const handleOnReceiveJson = (data: DataType[], bool: boolean) => {
    if (!data || !data.length || !bool) return null;
    const arr = data.map((d: DataType) => {
      let code: any = d.code;
      return {
        ...d,
        code,
      };
    });
    return arr;
  };

  /** @description 编辑已经存在的edge的label字段 */
  const handleEdgeUpdate = (pre: string, next: string, nodeId: string) => {
    if (pre === next) return;
    if (!nodeId) {
      let conditions = [...commonFormRef.current.conditions];
      let cIndex = _.findIndex(conditions, c => c.condition === pre);
      conditions[cIndex].condition = next;
      commonFormRef.current.setConditions(conditions);
      return;
    }
    childRef.current.updateEdge(pre, next, nodeId);
    let node = tableData[nodeId];
    if (!node) return;
    let newData = { ...tableData };
    let conditionIndex = newData[nodeId].condition.findIndex(n => n.condition === pre);
    newData[nodeId].condition[conditionIndex]['condition'] = next;
    setTableData(newData);
  }

  const leftContainerStyle = {
    width: `calc(100vw - ${rightContainerWidth}px)`,
    transition: 'all 0.3s',
  } as React.CSSProperties;

  const rightContainerStyle = {
    background: '#fff',
    padding: '20px',
    maxHeight: 'calc(100vh - 48px)',
    overflowY: 'auto',
    transition: 'all 0.3s',
    width: rightContainerWidth,
  } as React.CSSProperties;

  if (rightContainerWidth !== 500) rightContainerStyle['filter'] = 'blur(1.2rem)';

  const rightMask = {
    position: 'fixed',
    top: 48,
    right: 0,
    width: 50,
    height: 'calc(100vh - 48px)',
    zIndex: 1000,
    pointerEvents: 'none',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  } as React.CSSProperties;

  return (
    <DndProvider backend={HTML5Backend}>
      <div style={{ display: 'flex' }}>
        <Prompt when={isPrompt} message="你确定要离开吗？" />
        <div style={leftContainerStyle}>
          <ReactFlowArea
            ref={childRef}
            showNodeDetail={showNodeDetail}
            showEdgeDetail={showEdgeDetail}
            showJSONDrawer={showJSONDrawer}
          />
        </div>
        <div style={rightContainerStyle} onMouseEnter={() => setMouseEnterEditArea(true)} onMouseLeave={() => setMouseEnterEditArea(false)}>
          <Space>
            <Access accessible={access.administrator}>
              <Button type="primary" onClick={showJSON}>
                查看生成JSON
              </Button>
            </Access>
            <Button onClick={showNodesAndEdges}>节点与边</Button>
            <Access accessible={!workflowDetail?.id || (access.workflowSave && workflowDetail?.permit)}>
              <Button type="primary" onClick={save}>
                保存
              </Button>
            </Access>
            <Button onClick={handlePageBack}>返回</Button>
          </Space>
          <Row>
            <Col
              span={24}
              style={{
                borderTop: '1px solid rgba(0, 0, 0, 0.2)',
                marginTop: '20px',
              }}
            ></Col>
          </Row>
          <Row>
            <Col span={24}>
              <TitleTooltip title="流程图标题" />
              <Input
                placeholder="请输入流程图标题"
                value={baseConfig.name}
                onChange={(e) => dispatchBaseConfig({ type: 'update', prop: 'name', data: e.target.value })}
              />
            </Col>
            <Col span={24}>
              <TitleTooltip title="平台" />
              <Radio.Group
                options={[{ label: "企业微信", value: 1 }, { label: "小红书", value: 2 }]}
                onChange={(e) => dispatchBaseConfig({ type: 'update', prop: 'platform', data: e.target.value })}
                value={baseConfig.platform}
                optionType="button"
                buttonStyle="solid"
              />
            </Col>
            {
              baseConfig.platform === 1 ? <Col span={24}>
                <TitleTooltip title="消息引擎" />
                <Radio.Group
                  options={[{ label: "句子", value: 'juzi' }, { label: "玄风", value: 'xuanfeng' }]}
                  onChange={(e) => dispatchBaseConfig({ type: 'update', prop: 'messageEngine', data: e.target.value })}
                  value={baseConfig.messageEngine}
                  optionType="button"
                  buttonStyle="solid"
                />
              </Col> : null
            }
          </Row>
          <Row>
            <Col span={24}>
              <TitleTooltip
                title="全局规则"
                tooltip="全局匹配客户回复的内容，若匹配则直接进行处理而不按照既定顺序执行"
              />
              <CommonRule onRef={commonFormRef} />
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <TitleTooltip
                title="新增节点"
                tooltip="增加流程图内的节点（即话术，startNode表示开始，一个完整的流程必须要有一个startNode节点)"
              />
            </Col>
          </Row>
          <Row>
            {[
              { type: 'startNode', label: 'input' },
              { type: 'defaultNode', label: 'default' },
            ].map((vo) => (
              <Col key={vo.type} span={8}>
                <Button
                  style={{ width: '100%' }}
                  onClick={() => addNode(vo.type)}
                >
                  {vo.type}
                </Button>
              </Col>
            ))}
          </Row>
          <Row>
            <Col span={24}>
              {isShowNodeForm && currentNode && (
                <>
                  <TitleTooltip
                    title="编辑节点"
                    tooltip="编辑话术内容及客户回复内容的匹配规则"
                  />
                  <NodeForm
                    onRef={nodeFormRef}
                    initData={tableData}
                    node={currentNode}
                    setTableData={(data) => setTableData(data)}
                    nodeChange={nodeChange}
                  />
                </>
              )}
            </Col>
          </Row>
          <Row>
            <Col
              span={24}
              style={{
                borderTop: '1px solid rgba(0, 0, 0, 0.2)',
                marginTop: '20px',
              }}
            ></Col>
          </Row>
          <Row>【操作技巧】</Row>
          <Row>“线”上条件：表示若符合此条件则执行此步骤；</Row>
          <Row>删除：鼠标点击选中节点或线，按键盘【←】即可删除；</Row>
          <Row>编辑：鼠标点击选中节点即可在右侧输入框中进行编辑；</Row>
          <Row>
            复制节点：鼠标点击选中节点，command + c（window：Ctrl + c）；
          </Row>
          <Row>
            复制节点内容：鼠标左键长按内容左侧图标，拖动至想要复制到的节点后松开鼠标；
          </Row>
        </div>

        <JSONDrawer title={drawerTitle} setIsShowJSON={() => setIsShowJSON(false)} json={json} visible={isShowJSON} />

        {
          <ActionEditDrawer
            tableData={tableData}
            node={currentNode?.id ?? ''}
            setTableData={(data) => setTableData(data)}
            setCommonRuleData={(data) => {
              commonFormRef.current.saveTableData('onReceive', data);
            }}
            platform={baseConfig.platform}
          />
        }
        <ConditionEditModal handleEdgeUpdate={handleEdgeUpdate} />
        <Loading loading={loading} />
        {
          rightContainerWidth == 20 && <div style={rightMask}>
            <DoubleLeftOutlined style={{ fontSize: 24, color: '#666' }} />
          </div>
        }
      </div>
    </DndProvider>
  );
};

export default OverviewFlow;
